/**
 * Session Management & Remember Me Functionality
 * Manages automatic token refresh for both standard and extended sessions
 * - Standard session (no remember me): 1 day with auto-refresh
 * - Remember me session: 1 week with auto-refresh
 */

import { logout } from "./auth-utils"
import { TokenRefresh } from "./token-refresh"

const REMEMBER_ME_DURATION = 7 * 24 * 60 * 60 * 1000 // 1 week in milliseconds
const STANDARD_SESSION_DURATION = 24 * 60 * 60 * 1000 // 1 day in milliseconds
const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000 // Refresh every 45 minutes (tokens expire in 60 min)

let refreshIntervalId: NodeJS.Timeout | null = null

export class RememberMe {
  /**
   * Check if remember me is enabled and still valid
   */
  static isRememberMeActive(): boolean {
    const rememberMe = localStorage.getItem("remember_me")
    const timestamp = localStorage.getItem("remember_me_timestamp")

    if (rememberMe !== "true" || !timestamp) {
      return false
    }

    const now = Date.now()
    const sessionStart = parseInt(timestamp, 10)
    const sessionAge = now - sessionStart

    // Check if remember me session has expired (> 1 week)
    if (sessionAge > REMEMBER_ME_DURATION) {
      this.clearRememberMe()
      return false
    }

    return true
  }

  /**
   * Check if standard session (no remember me) is active and still valid
   */
  static isStandardSessionActive(): boolean {
    const sessionTimestamp = localStorage.getItem("session_timestamp")
    const rememberMe = localStorage.getItem("remember_me")

    // If remember me is enabled, use that instead
    if (rememberMe === "true") {
      return false
    }

    if (!sessionTimestamp) {
      return false
    }

    const now = Date.now()
    const sessionStart = parseInt(sessionTimestamp, 10)
    const sessionAge = now - sessionStart

    // Check if standard session has expired (> 1 day)
    if (sessionAge > STANDARD_SESSION_DURATION) {
      this.clearSession()
      return false
    }

    return true
  }

  /**
   * Check if any session (remember me or standard) is active
   */
  static isSessionActive(): boolean {
    return this.isRememberMeActive() || this.isStandardSessionActive()
  }

  /**
   * Clear remember me data
   */
  static clearRememberMe(): void {
    localStorage.removeItem("remember_me")
    localStorage.removeItem("remember_me_timestamp")
  }

  /**
   * Clear session data
   */
  static clearSession(): void {
    localStorage.removeItem("session_timestamp")
    this.clearRememberMe()
  }

  /**
   * Start automatic token refresh for all sessions (remember me or standard)
   */
  static startAutoRefresh(): void {
    // Don't start if no active session or already running
    if (!this.isSessionActive() || refreshIntervalId) {
      return
    }

    // Immediately check and refresh if needed
    this.checkAndRefresh()

    // Set up periodic refresh
    refreshIntervalId = setInterval(() => {
      this.checkAndRefresh()
    }, TOKEN_REFRESH_INTERVAL)
  }

  /**
   * Stop automatic token refresh
   */
  static stopAutoRefresh(): void {
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId)
      refreshIntervalId = null
    }
  }

  /**
   * Check if refresh is needed and perform it
   */
  private static async checkAndRefresh(): Promise<void> {
    // Check if any session is still valid
    if (!this.isSessionActive()) {
      this.stopAutoRefresh()
      logout()
      return
    }

    // Check if token is expired or about to expire
    if (TokenRefresh.isTokenExpired()) {
      const success = await TokenRefresh.refreshTokens()

      if (!success) {
        this.stopAutoRefresh()
        logout()
      }
    }
  }

  /**
   * Initialize session management on app load
   * Call this in your root component or AuthGuard
   * Handles both remember me (1 week) and standard (1 day) sessions
   */
  static initialize(): void {
    if (typeof window === "undefined") return

    if (this.isRememberMeActive()) {
      this.startAutoRefresh()
    } else if (this.isStandardSessionActive()) {
      this.startAutoRefresh()
    }
  }

  /**
   * Get remaining time for current session (remember me or standard)
   */
  static getRemainingTime(): {
    days: number
    hours: number
    minutes: number
    type: "remember_me" | "standard"
  } | null {
    let timestamp: string | null = null
    let duration: number
    let type: "remember_me" | "standard"

    if (this.isRememberMeActive()) {
      timestamp = localStorage.getItem("remember_me_timestamp")
      duration = REMEMBER_ME_DURATION
      type = "remember_me"
    } else if (this.isStandardSessionActive()) {
      timestamp = localStorage.getItem("session_timestamp")
      duration = STANDARD_SESSION_DURATION
      type = "standard"
    } else {
      return null
    }

    if (!timestamp) {
      return null
    }

    const now = Date.now()
    const sessionStart = parseInt(timestamp, 10)
    const sessionAge = now - sessionStart
    const remaining = duration - sessionAge

    const days = Math.floor(remaining / (24 * 60 * 60 * 1000))
    const hours = Math.floor(
      (remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
    )
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000))

    return { days, hours, minutes, type }
  }
}
